import { useCallback, useState } from "react";
import type { Lang } from "../types";

export interface ChatMsg {
  role: "user" | "ai";
  text: string;
}

const RESPONSES: Record<Lang, Record<string, string>> = {
  en: {
    muscle: "For muscle gain, I recommend: Whey Protein post-workout (25g protein), Creatine 5g daily, and BCAAs during training. This stack covers protein synthesis, strength, and recovery.",
    weight: "For weight loss, focus on: high protein intake (Whey Isolate), stay hydrated with Electrolyte Mix, and Magnesium at night for better sleep — sleep quality directly affects fat loss hormones.",
    energy: "For sustained energy: Ceremonial Matcha in the morning (clean caffeine + L-theanine), Pre-Workout Ignite before gym, and Electrolytes during exercise. Avoid combining matcha and pre-workout on the same day.",
    default: "I'd be happy to help with your supplement questions! Tell me your goals, dietary restrictions, or any specific products you're curious about.",
  },
  es: {
    muscle: "Para ganar músculo recomiendo: Proteína Whey post-entrenamiento (25g), Creatina 5g diario, y BCAAs durante el entrenamiento. Este stack cubre síntesis proteica, fuerza y recuperación.",
    weight: "Para bajar de peso: alta ingesta de proteína (Whey Aislada), hidratación con Electrolitos, y Magnesio de noche para mejor sueño — la calidad del sueño afecta directamente las hormonas de pérdida de grasa.",
    energy: "Para energía sostenida: Matcha Ceremonial en la mañana (cafeína limpia + L-teanina), Pre-Entreno Ignite antes del gym, y Electrolitos durante ejercicio. Evita combinar matcha y pre-entreno el mismo día.",
    default: "¡Encantado de ayudarte con tus preguntas de suplementos! Cuéntame tus objetivos, restricciones dietéticas, o productos específicos que te interesen.",
  },
};

function pickKey(input: string): "muscle" | "weight" | "energy" | "default" {
  const s = input.toLowerCase();
  if (s.includes("musc")) return "muscle";
  if (s.includes("peso") || s.includes("weight")) return "weight";
  if (s.includes("energ")) return "energy";
  return "default";
}

/** Local AI chat fallback (sim). Real coach lives in NutriCoachHub. */
export function useAIChat(lang: Lang) {
  const [chatMsgs, setChatMsgs] = useState<ChatMsg[]>([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  const sendChat = useCallback(() => {
    if (!chatInput.trim()) return;
    const userMsg = chatInput;
    setChatMsgs((p) => [...p, { role: "user", text: userMsg }]);
    setChatInput("");
    setChatLoading(true);
    setTimeout(() => {
      const key = pickKey(userMsg);
      setChatMsgs((p) => [...p, { role: "ai", text: RESPONSES[lang][key] }]);
      setChatLoading(false);
    }, 1500);
  }, [chatInput, lang]);

  return { chatMsgs, setChatMsgs, chatInput, setChatInput, chatLoading, sendChat };
}